import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PetCategoryBar from './components/PetCategoryBar';
import PetCard from './components/PetCard';
import PetDetailModal from './components/PetDetailModal';
import UploadListingModal from './components/UploadListingModal';
import { petListings } from '../../mocks/petListings';
import SEOHead from '../../components/SEOHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const petsSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Pets for Adoption & Sale - Fluffy Pets',
  url: `${SITE_URL}/pets`,
  description:
    'Browse dogs, puppies, cats, rabbits, birds, and turtles available for adoption or sale. Find your perfect pet companion at Fluffy Pets.',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Fluffy Pets',
    url: SITE_URL,
  },
  about: [
    { '@type': 'Thing', name: 'Dogs' },
    { '@type': 'Thing', name: 'Puppies' },
    { '@type': 'Thing', name: 'Cats' },
    { '@type': 'Thing', name: 'Rabbits' },
    { '@type': 'Thing', name: 'Birds' },
    { '@type': 'Thing', name: 'Turtles' },
  ],
};

interface Pet {
  id: number;
  name: string;
  category: string;
  breed: string;
  age: string;
  gender: string;
  price: number;
  location: string;
  description: string;
  image: string;
  seller: string;
  featured: boolean;
}

export default function PetsPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const filteredPets = useMemo(() => {
    let result = petListings;
    if (selectedCategory !== 'all') {
      result = result.filter((p) => p.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.breed.toLowerCase().includes(q) ||
          p.location.toLowerCase().includes(q)
      );
    }
    return result;
  }, [selectedCategory, searchQuery]);

  const featuredPets = filteredPets.filter((p) => p.featured);
  const regularPets = filteredPets.filter((p) => !p.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F9] via-[#F8F4FC] to-[#FFF5F9]">
      <SEOHead
        title="Pets for Adoption &amp; Sale | Dogs, Cats, Rabbits, Birds - Fluffy Pets"
        description="Find dogs, puppies, cats, rabbits, birds, and turtles available for adoption or sale. Browse our pet listings and connect with sellers to find your perfect companion."
        keywords="pets for adoption, dogs for sale, cats for adoption, rabbits birds turtles, pet listings, buy pets online"
        canonical={`${SITE_URL}/pets`}
        schema={petsSchema}
      />
      {/* Navbar */}
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img
                src="https://static.readdy.ai/image/8ba2fb49baf129fe47398873ffc1b622/7319ff1316bfcbedde225c0cf8d58231.jpeg"
                alt="Fluffy Pets Logo"
                className="w-16 h-16 object-contain"
              />
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowUpload(true)}
                className="bg-gradient-to-r from-[#9B4C9A] to-[#FF9BC8] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                <i className="ri-add-line text-lg"></i>
                List Your Pet
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-[#9B4C9A] hover:text-[#FF9BC8] transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-arrow-left-line text-xl"></i>
                <span className="font-semibold text-sm">Home</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/51340c6cc64194622dbbbce96aae4567.jpg"
            alt="Pets Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#9B4C9A]/80 via-[#9B4C9A]/60 to-[#FF9BC8]/50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 text-center">
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg"
            style={{ fontFamily: 'Pacifico, cursive' }}
          >
            Find Your Perfect Pet
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed">
            Browse our collection of adorable dogs, puppies, cats, rabbits, birds, and turtles waiting for their forever home
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-[#8B7BA8] text-lg"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, breed, or location..."
              className="w-full pl-14 pr-6 py-4 rounded-full bg-white/95 backdrop-blur-sm text-sm text-[#4A3B5C] placeholder-[#8B7BA8]/60 focus:outline-none focus:ring-2 focus:ring-[#FF9BC8] shadow-xl"
            />
          </div>
        </div>
      </section>

      {/* Category Bar */}
      <PetCategoryBar selected={selectedCategory} onSelect={setSelectedCategory} />

      {/* Upload CTA Banner */}
      <section className="px-6 pb-8">
        <div className="max-w-7xl mx-auto">
          <div
            onClick={() => setShowUpload(true)}
            className="bg-white border-2 border-dashed border-[#E6E0F5] rounded-2xl p-8 text-center cursor-pointer hover:border-[#9B4C9A] hover:bg-[#F8F6FB] transition-all group"
          >
            <i className="ri-upload-cloud-2-line text-4xl text-[#9B4C9A] group-hover:scale-110 transition-transform block mb-2"></i>
            <h3 className="text-lg font-bold text-[#4A3B5C] mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Have a pet to list?
            </h3>
            <p className="text-sm text-[#8B7BA8]">
              Upload photos and details to connect with potential adopters
            </p>
          </div>
        </div>
      </section>

      {/* Featured Pets */}
      {featuredPets.length > 0 && (
        <section className="px-6 pb-10">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-[#4A3B5C] mb-6 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <i className="ri-star-fill text-[#F5A623] w-6 h-6 flex items-center justify-center"></i>
              Featured Pets
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-product-shop>
              {featuredPets.map((pet) => (
                <PetCard key={pet.id} pet={pet} onViewDetails={setSelectedPet} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Pets */}
      <section className="px-6 pb-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-[#4A3B5C] mb-6 flex items-center gap-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            <i className="ri-heart-line text-[#9B4C9A] w-6 h-6 flex items-center justify-center"></i>
            {selectedCategory === 'all' ? 'All Pets' : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}`}
            <span className="text-sm font-normal text-[#8B7BA8] ml-2">({filteredPets.length} listings)</span>
          </h2>

          {filteredPets.length === 0 ? (
            <div className="text-center py-20">
              <i className="ri-search-line text-6xl text-[#E6E0F5] block mb-4"></i>
              <h3 className="text-xl font-bold text-[#4A3B5C] mb-2">No pets found</h3>
              <p className="text-sm text-[#8B7BA8]">Try adjusting your search or category filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
              {(regularPets.length > 0 ? regularPets : filteredPets).map((pet) => (
                <PetCard key={pet.id} pet={pet} onViewDetails={setSelectedPet} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selectedPet && (
        <PetDetailModal pet={selectedPet} onClose={() => setSelectedPet(null)} />
      )}

      {/* Upload Modal */}
      {showUpload && (
        <UploadListingModal onClose={() => setShowUpload(false)} />
      )}
    </div>
  );
}
